/**
 * navigation/platformAuthRedirect.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Upsell does NOT own authentication. SK Platform owns the only
 * login / signup / forgot-password screens for every product. When this app
 * has no valid session, we bounce to the centralized platform auth carrying
 * the product handoff:
 *     ?product=upsell&return_to=<where they were>&state=<nonce>
 * plus a short-lived, parent-domain `sk_auth_state` cookie the platform
 * verifies (CSRF / first-party initiation). After auth, the platform redirects
 * back here, signed in via the shared session cookie.
 *
 * Web-only; no-op on native. This mirrors SK-Catalog's pattern exactly, with
 * PRODUCT switched to 'upsell'.
 */
import { Platform } from 'react-native';

const IS_LOCAL =
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const PLATFORM_AUTH_URL = IS_LOCAL ? 'http://localhost:3009/' : 'https://platform.staging.softknack.com/';
const PRODUCT = 'upsell';
const STATE_COOKIE = 'sk_auth_state';

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function randomState(): string {
  const bytes = new Uint8Array(16);
  (window.crypto as Crypto).getRandomValues(bytes);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

function setStateCookie(state: string): void {
  const parts = window.location.hostname.split('.');
  const domain =
    parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
      ? `; domain=.${parts.slice(-2).join('.')}`
      : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATE_COOKIE}=${state}; Max-Age=600; path=/; SameSite=Lax${domain}${secure}`;
}

let redirecting = false;

/** Bounce to the centralized platform auth, returning here after sign-in. */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || redirecting) return;
  redirecting = true;

  const state = randomState();
  setStateCookie(state);

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  // eslint-disable-next-line no-console
  console.info('[auth-handoff] SK-UPSELL → platform:', { product: PRODUCT, return_to: window.location.href, to: url.toString() });
  window.location.assign(url.toString());
}
